import { useEffect, useState } from 'preact/hooks';
import { defaultRelayUrl } from '../telemetry/RemoteTelemetryProvider';
import { relayKeyForRoom, forgetRoomKey } from './relayKey';

export interface RelayStatus {
  connected: boolean; // this page reached the relay
  pubs: number; // bridges publishing into the room
  hz: number; // telemetry rate seen in the last second
  lastAgeMs: number | null;
  /** the bridge sees CAN frames from the car (Commander connected) */
  canOk: boolean;
  /** RaceBox 25 Hz GNSS riding the snapshot */
  raceboxOk: boolean;
  /** generic OBD-II dongle riding the snapshot */
  obdOk: boolean;
  /** live connection PROGRESS from the app's bridge: off|scanning|connecting|connected|lost */
  rbStatus: string | null;
  obdStatus: string | null;
  /** Commander answered over Wi-Fi, CAN frames pending */
  cmAnswering: boolean;
  /** the Commander's Wi-Fi link as the phone sees it: off | knock (no reply) | rx */
  pdSt: string | null;
  pdErr: string | null;
  /** screens subscribed to this room (this page counts as one) */
  screens: number;
  gear: string | null;
}

/** Subscribe (read-only) to a relay room to show whether a CAN bridge is live. */
export function useRelayStatus(room: string, enabled = true): RelayStatus {
  const [st, setSt] = useState<RelayStatus>({ connected: false, pubs: 0, hz: 0, lastAgeMs: null, canOk: false, raceboxOk: false, obdOk: false, rbStatus: null, obdStatus: null, cmAnswering: false, pdSt: null, pdErr: null, screens: 0, gear: null });
  useEffect(() => {
    if (!enabled || !room) return;
    let ws: WebSocket | null = null;
    let closed = false;
    let count = 0;
    let lastMsg = 0;
    let pubs = 0;
    let connected = false;
    // per-device hysteresis (Marc round 2: 5 s): a flag stays ON until missing for 5 s straight —
    // a green light that blinks reads as broken, so it must not blink
    let lastCanAt = 0; let lastRbAt = 0; let lastObdAt = 0; let gear: string | null = null;
    let rbStatus: string | null = null; let obdStatus: string | null = null; let lastCmPkAt = 0; let screens = 0;
    let pdSt: string | null = null; let pdErr: string | null = null;
    const tick = setInterval(() => {
      const now = Date.now(); setSt({ connected, pubs, hz: count, lastAgeMs: lastMsg ? now - lastMsg : null, canOk: now - lastCanAt < 5000, raceboxOk: now - lastRbAt < 5000, obdOk: now - lastObdAt < 5000, rbStatus, obdStatus, cmAnswering: now - lastCmPkAt < 5000, pdSt, pdErr, screens, gear });
      count = 0;
    }, 1000);
    // the relay wants the room's key: fetch it once, then open (and keep the retry loop as it was)
    let key = '';
    const open = () => {
      if (closed) return;
      if (!key) {
        void relayKeyForRoom(room)
          .then((k) => { key = k; if (!closed) open(); })
          .catch(() => { if (!closed) setTimeout(open, 4000); });
        return;
      }
      try {
        ws = new WebSocket(`${defaultRelayUrl(room)}?role=sub&key=${encodeURIComponent(key)}`);
      } catch {
        return;
      }
      let opened = false;
      ws.onopen = () => { connected = true; opened = true; };
      ws.onclose = () => { connected = false; pubs = 0; if (!opened) { forgetRoomKey(room); key = ''; } if (!closed) setTimeout(open, 2000); };
      ws.onerror = () => { /* onclose follows */ };
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(String(ev.data));
          if (m.type === 'relay') { pubs = m.pubs ?? 0; return; }
          if (m.type === 'telemetry') { count++; lastMsg = Date.now(); pubs = Math.max(pubs, 1); if (m.canOk) lastCanAt = lastMsg; if (m.raceboxOk) lastRbAt = lastMsg; if (m.obdOk) lastObdAt = lastMsg; if (m.cmPk) lastCmPkAt = lastMsg; if (typeof m.rbSt === 'string') rbStatus = m.rbSt; if (typeof m.screens === 'number') screens = m.screens; if (typeof m.obdSt === 'string') obdStatus = m.obdSt; if (typeof m.pdSt === 'string') pdSt = m.pdSt; if (typeof m.pdErr === 'string') pdErr = m.pdErr; gear = typeof m.gear === 'string' ? m.gear : null; }
        } catch { /* ignore */ }
      };
    };
    open();
    return () => { closed = true; clearInterval(tick); try { ws?.close(); } catch { /* ignore */ } };
  }, [room, enabled]);
  return st;
}

export function newRoomCode(): string {
  const abc = 'abcdefghjkmnpqrstuvwxyz23456789';
  let s = '';
  for (let i = 0; i < 6; i++) s += abc[Math.floor(Math.random() * abc.length)];
  return s;
}
